import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useDebounce } from '@uidotdev/usehooks';
import { getAccount, type Board, getFirstBoardItemsPage } from '@gorilla/common/src/lib/monday-api/api';
import useSWRMutation from 'swr/mutation';
import { type PortalSettings, DEFAULT_PORTAL_SETTINGS, PortalSettingsSchema } from '@gorilla/portal-shared/src/schemas';
import { extractErrorMessage, getUsedPlaceholderColumnTypes } from '../lib/utils';
import { monday, mondayClient } from '../services/monday';
import {
  getPortal,
  saveSettings,
  publishPortal,
  unpublishPortal,
  discardDraft,
  getMe,
  uploadFile,
  getRegistryEntries,
  connectCustomDomain,
  disconnectCustomDomain,
  verifyCustomDomain,
  updatePortalRegion,
} from '../services/portal';
import { useBoard } from '../shared/hooks/use-boards';
import { useAnalytics } from './use-analytics';

export type MondayContext = {
  boardId: number;
  user: {
    id: string;
    isAdmin: true;
  };
};

function getSubitemsBoardId(mainBoard: Board) {
  const subtasksColumn = mainBoard.columns.find((column) => column.type === 'subtasks');

  if (!subtasksColumn) {
    return null;
  }

  try {
    const settings = JSON.parse(subtasksColumn.settings_str);
    if (settings.boardIds && settings.boardIds.length) {
      return settings.boardIds[0] as number;
    }
  } catch (err) {}

  return null;
}

export function getBoardSourceIdsInUse(portalSettings: PortalSettings) {
  return _.uniq(
    portalSettings.pages.flatMap((page) =>
      page.sections.flatMap((section) =>
        Object.values(section.fieldValues).flatMap((fieldValue) => {
          if (fieldValue && fieldValue.boardSourceId) {
            // TODO: check the field type instead of assuming it's a board source field based on the value
            return [fieldValue.boardSourceId];
          }
          return [];
        }),
      ),
    ),
  );
}

// get authErrorCode query param
export function getAuthErrorCode() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('authErrorCode');
}

type PortalInitState = { state: 'init' };
type PortalReadyState = { state: 'ready' };
type PortalErrorState = {
  state: 'error';
  errorKind:
    | 'MONDAY_CONTEXT'
    | 'CLIENT_BOARD'
    | 'MONDAY_ACCOUNT'
    | 'ME'
    | 'AUTHENTICATION'
    | 'MISSING_INTEGRATION_USER'
    | 'INVALID_INTEGRATION_USER'
    | 'INTEGRATION_USER_NOT_BOARD_OWNER'
    | 'INVALID_BOARD_OWNERS'
    | 'USER_NOT_OWNER'
    | 'PORTAL_SERVER'
    | 'ANALYTICS'
    | 'PORTAL_SETTINGS_INVALID';
  error: string;
  rawError?: any;
};
type PortalState = PortalInitState | PortalReadyState | PortalErrorState;

export function usePortal() {
  const [settingsDraft, _setSettingsDraft] = useState<PortalSettings | null>(null);
  const settingsDraftDeferred = useDebounce(settingsDraft, 200);
  const [shouldForceUiUpdate, setShouldForceUiUpdate] = useState(false);
  const [uiUpdateId, setUiUpdateId] = useState<number>(1);
  const updatedRegionRef = useRef<boolean>(false);

  const { data: mondayContext, error: mondayContextError } = useSWR(
    `/monday-context`,
    async () => {
      const mContext = ((await monday.get('context')) as any).data as MondayContext;

      if (!mContext) {
        throw new Error('This app can only be used inside of monday.com');
      }

      return mContext;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { board: clientBoard, error: clientBoardError } = useBoard(mondayContext?.boardId);
  const clientBoardSubitemsBoardId = clientBoard ? getSubitemsBoardId(clientBoard) : null;

  function setSettingsDraft(settings: PortalSettings, forceUiUpdate = false) {
    if (!clientBoard) {
      throw new Error('Client board is not loaded');
    }

    const placeholderColumnTypes = getUsedPlaceholderColumnTypes(clientBoard, settings);
    _setSettingsDraft({ ...settings, placeholderColumnTypes });

    if (forceUiUpdate) {
      setShouldForceUiUpdate(true);
    }
  }

  const { data: mondayAccount, error: mondayAccountError } = useSWR(
    `/monday-account`,
    async () => {
      return await getAccount(mondayClient);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const {
    data: portal,
    mutate: fetchPortal,
    isLoading: isPortalLoading,
    error: portalServerError,
  } = useSWR(
    mondayContext?.boardId ? `/board/get/draft` : null,
    async () => {
      return await getPortal(mondayContext!.boardId, 'draft');
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  // TODO: clientBoardItemsError is not handled
  const {
    data: clientBoardItems,
    error: clientBoardItemsError,
    isLoading: isLoadingClientBoardItems,
  } = useSWR(
    mondayContext?.boardId ? `/client-board-items/${mondayContext?.boardId}` : null,
    async () => {
      const firstPage = await getFirstBoardItemsPage(mondayClient, mondayContext!.boardId, 20, '', false, {
        withoutBoardRelationValue: true,
        withoutMirrorValue: true,
      });

      return firstPage.items;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { analyticsData, error: analyticsError } = useAnalytics(portal ? mondayContext?.boardId : undefined);

  const {
    data: registryEntries,
    mutate: fetchRegistryEntries,
    isLoading: areRegistryEntriesLoading,
    error: registryEntriesError,
  } = useSWR(
    mondayContext?.boardId && portal ? `/registry-entries/${mondayContext?.boardId}` : null,
    async () => {
      return await getRegistryEntries(mondayContext!.boardId);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { data: me, error: meError } = useSWR(
    '/me',
    async () => {
      return await getMe();
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const {
    trigger: triggerVerifyCustomDomain,
    isMutating: isVerifyingCustomDomain,
    error: verifyCustomDomainError,
  } = useSWRMutation('/verify-custom-domain', async () => {
    if (mondayContext?.boardId) {
      try {
        await verifyCustomDomain(mondayContext.boardId);
        await fetchRegistryEntries();
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    }
  });

  const {
    trigger: triggerConnectCustomDomain,
    isMutating: isConnectCustomDomainConnecting,
    error: customDomainError,
  } = useSWRMutation('/connect-custom-domain', async (_key, { arg }) => {
    if (mondayContext?.boardId) {
      try {
        await connectCustomDomain(mondayContext.boardId, arg);
        await fetchRegistryEntries();
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    }
  });

  const {
    trigger: triggerDisconnectCustomDomain,
    isMutating: isConnectCustomDomainDisconnecting,
    error: disconnectCustomDomainError,
  } = useSWRMutation('/disconnect-custom-domain', async () => {
    if (mondayContext?.boardId) {
      try {
        await disconnectCustomDomain(mondayContext.boardId);
        await fetchRegistryEntries();
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    }
  });

  const {
    trigger: triggerUploadLogo,
    isMutating: isUploadingLogo,
    error: uploadingLogoError,
  } = useSWRMutation('/upload-logo', async (_key, { arg: fileSettings }: { arg: { name: string; content: string } }) => {
    try {
      const { hash } = await uploadFile(mondayContext?.boardId!, fileSettings);
      const newSettingsDraft = _.cloneDeep(portalSettings);
      newSettingsDraft.theme.logo = hash;
      setSettingsDraft(newSettingsDraft);

      return hash;
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  const {
    trigger: triggerSaveSettings,
    isMutating: isSavingSettings,
    error: saveSettingsError,
  } = useSWRMutation('/save-settings', async (_key, { arg: argPortalSettings }: { arg?: PortalSettings }) => {
    // TODO: user could have changed the settings while we were saving
    const settingsToSave = argPortalSettings || settingsDraftDeferred;

    if (!settingsToSave) {
      return;
    }

    try {
      const result = await saveSettings(mondayContext?.boardId!, settingsToSave);
      fetchPortal(result, { revalidate: false });

      // Update the region on first save.
      if (mondayContext?.boardId && !updatedRegionRef.current) {
        updatePortalRegion(mondayContext?.boardId).then(() => {
          updatedRegionRef.current = true;
        });
      }
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  const {
    trigger: publish,
    isMutating: isPublishing,
    error: publishServerError,
  } = useSWRMutation('/publish', async () => {
    try {
      monday.execute('valueCreatedForUser');
      const result = await publishPortal(mondayContext!.boardId);
      fetchPortal(result, { revalidate: false });
      return result;
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  const {
    trigger: unpublish,
    isMutating: isUnpublishing,
    error: unpublishServerError,
  } = useSWRMutation('/unpublish', async () => {
    try {
      const result = await unpublishPortal(mondayContext!.boardId);
      fetchPortal(result, { revalidate: false });
      return result;
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  const {
    trigger: triggerDiscardDraft,
    isMutating: isDiscardingDraft,
    error: discardDraftError,
  } = useSWRMutation('/discard-draft', async () => {
    try {
      const result = await discardDraft(mondayContext!.boardId);
      fetchPortal(result, { revalidate: false });
      _setSettingsDraft(null);
      enqueueSnackbar('Draft discarded', { variant: 'success' });
      return result;
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  useEffect(() => {
    if (settingsDraftDeferred) {
      triggerSaveSettings();
      if (shouldForceUiUpdate) {
        setShouldForceUiUpdate(false);
        setUiUpdateId((id) => id + 1);
      }
    } else {
      // when the draft was discardet make sure to clear any frontend state
      setUiUpdateId((id) => id + 1);
    }
  }, [settingsDraftDeferred]);

  const portalSettings = settingsDraftDeferred || portal?.settings || DEFAULT_PORTAL_SETTINGS;
  if (portalSettings.theme && _.difference(Object.keys(DEFAULT_PORTAL_SETTINGS.theme), Object.keys(portalSettings.theme)).length > 0) {
    portalSettings.theme = { ...DEFAULT_PORTAL_SETTINGS.theme, ...portalSettings.theme };
  }
  const hasUnpublishedChanges = portal && (portal.lastPublishedAt || 0) < (portal.lastUpdatedAt || 0);
  const isPortalPublished = !!portal?.lastPublishedAt;
  const authErrorCode = getAuthErrorCode();
  const areServerPortalSettingsValid = portal?.settings ? PortalSettingsSchema.safeParse(portal?.settings).success : true;

  const boardSourceIdsInUse = getBoardSourceIdsInUse(portalSettings);

  // @ts-ignore
  let region = mondayContext && mondayContext?.region ? mondayContext?.region.substring(0, 2) : 'us';

  if (region !== 'us' && region !== 'eu' && region !== 'il') {
    // for now, default to 'us' for unsupported regions
    region = 'us';
  }

  let portalState: PortalState = {
    state: 'init',
  };

  if (authErrorCode) {
    let errorMessage = 'An error occurred while trying to authenticate with monday.com';

    if (authErrorCode === 'INTEGRATION_USER_NOT_BOARD_OWNER') {
      errorMessage = 'Only board owners can be set as integration users.';
    } else if (authErrorCode === 'INVALID_BOARD_ID') {
      errorMessage = 'Invalid board ID.';
    } else if (authErrorCode === 'OAUTH_EXCHANGE_FAILED') {
      errorMessage = 'monday.com rejected the authorization. Please try authorizing again.';
    } else if (authErrorCode === 'OAUTH_TOKEN_STORAGE_FAILED') {
      errorMessage = 'The authorization succeeded but could not be saved. Please try again in a moment.';
    }

    portalState = {
      state: 'error',
      errorKind: 'AUTHENTICATION',
      error: errorMessage,
    };
  } else if (!areServerPortalSettingsValid) {
    portalState = {
      state: 'error',
      errorKind: 'PORTAL_SETTINGS_INVALID',
      error: 'The portal settings are invalid. Please fix the errors and try again.',
    };
  } else if (portalServerError) {
    if (portalServerError?.code === 'MISSING_INTEGRATION_USER') {
      portalState = {
        state: 'error',
        errorKind: 'MISSING_INTEGRATION_USER',
        error: 'No integration user set. Set an integration user to continue.',
      };
    } else if (portalServerError?.code === 'INVALID_INTEGRATION_USER') {
      portalState = {
        state: 'error',
        errorKind: 'INVALID_INTEGRATION_USER',
        error:
          'Invalid integration user set. Set a valid integration user to continue. This might also be a temporary issue with monday.com.',
      };
    } else if (portalServerError?.code === 'INTEGRATION_USER_NOT_BOARD_OWNER') {
      portalState = {
        state: 'error',
        errorKind: 'INTEGRATION_USER_NOT_BOARD_OWNER',
        error: 'Only board owners can be set as integration users.',
      };
    } else if (portalServerError?.code === 'USER_NOT_OWNER') {
      portalState = {
        state: 'error',
        errorKind: 'USER_NOT_OWNER',
        error: 'You have to be an owner of this board to manage the portal.',
      };
    } else if (portalServerError?.code === 'INVALID_BOARD_OWNERS') {
      portalState = {
        state: 'error',
        errorKind: 'INVALID_BOARD_OWNERS',
        error: 'The board owners could not be determined. Please check the board settings and try again.',
      };
    } else {
      portalState = {
        state: 'error',
        errorKind: 'PORTAL_SERVER',
        error: extractErrorMessage(portalServerError, 'An error occurred while fetching the portal'),
        rawError: portalServerError,
      };
    }
  } else if (meError) {
    portalState = {
      state: 'error',
      errorKind: 'ME',
      error: extractErrorMessage(meError, 'An error occurred while fetching the user'),
      rawError: meError,
    };
  } else if (mondayContextError) {
    portalState = {
      state: 'error',
      errorKind: 'MONDAY_CONTEXT',
      error: extractErrorMessage(mondayContextError, 'An error occurred while fetching monday context'),
      rawError: mondayContextError,
    };
  } else if (clientBoardError) {
    portalState = {
      state: 'error',
      errorKind: 'CLIENT_BOARD',
      error: extractErrorMessage(clientBoardError, 'An error occurred while fetching client board'),
      rawError: clientBoardError,
    };
  } else if (mondayAccountError) {
    portalState = {
      state: 'error',
      errorKind: 'MONDAY_ACCOUNT',
      error: extractErrorMessage(mondayAccountError, 'An error occurred while fetching monday account'),
      rawError: mondayAccountError,
    };
  } else if (analyticsError) {
    portalState = {
      state: 'error',
      errorKind: 'ANALYTICS',
      error: extractErrorMessage(analyticsError, 'An error occurred while fetching analytics data'),
      rawError: analyticsError,
    };
  }

  if (portalState.state !== 'error' && clientBoard && mondayAccount && portal && me && analyticsData) {
    portalState = {
      state: 'ready',
    };
  }

  return {
    portal,
    region,
    isPortalLoading,
    mondayContext,
    mondayContextError,
    portalServerError,
    triggerSaveSettings,
    isSavingSettings,
    setSettingsDraft,
    portalSettings,
    uiUpdateId,
    publish,
    isPublishing,
    unpublish,
    isUnpublishing,
    publishServerError,
    unpublishServerError,
    saveSettingsError,
    hasUnpublishedChanges,
    isPortalPublished,
    mondayAccount,
    mondayAccountError,
    authErrorCode,
    me,
    meError,
    boardSourceIdsInUse,
    areServerPortalSettingsValid,
    clientBoard,
    clientBoardSubitemsBoardId,
    triggerUploadLogo,
    isUploadingLogo,
    uploadingLogoError,
    clientBoardItems,
    clientBoardItemsError,
    isLoadingClientBoardItems,

    portalState,

    registryEntries,
    triggerVerifyCustomDomain,
    areRegistryEntriesLoading,
    registryEntriesError,
    isVerifyingCustomDomain,
    verifyCustomDomainError,
    triggerConnectCustomDomain,
    isConnectCustomDomainConnecting,
    customDomainError,
    triggerDisconnectCustomDomain,
    isConnectCustomDomainDisconnecting,
    disconnectCustomDomainError,

    triggerDiscardDraft,
    isDiscardingDraft,
    discardDraftError,

    analyticsData,
  };
}
